import { useMemo, useState } from "react";
import type { Kunde, Projekt } from "../../lib/types";
import { comboBtn, comboBtnText, comboItem, comboList, comboNeu, input, ruhendChip } from "./planer-styles";
import { istRuhend } from "./planer-utils";

// ── Projekt-Suchfeld ───────────────────────────────────────────────────────
/** `leerLabel` gesetzt = das Feld darf leer bleiben (Filter &bdquo;Alle Projekte&ldquo;). */
export function ProjektCombo({ projekte, value, onChange, leerLabel, platzhalter, onNeu }: {
  projekte: Projekt[];
  value: string;
  onChange: (id: string) => void;
  leerLabel?: string;
  platzhalter?: string;
  /* Gesetzt = das Feld darf anlegen, was es nicht findet. Bekommt mit, was
     gerade gesucht wurde, damit die Maske den Namen schon trägt. */
  onNeu?: (vorschlag: string) => void;
}) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const sel = projekte.find((p) => p.id === value);
  const list = useMemo(() => {
    const s = q.trim().toLowerCase();
    /* Aktive zuerst: fuer die Nachpflege muessen abgeschlossene Projekte
       auffindbar sein, die taegliche Planung sollen sie aber nicht zumuellen. */
    const gefiltert = s
      ? projekte.filter((p) => `${p.title} ${p.kunde_name}`.toLowerCase().includes(s))
      : projekte;
    return [...gefiltert]
      .sort((a, b) => Number(istRuhend(a.status)) - Number(istRuhend(b.status)))
      .slice(0, 60);
  }, [projekte, q]);
  return (
    <div style={{ position: "relative" }}>
      {(sel || leerLabel) && !open ? (
        <button onClick={() => { setOpen(true); setQ(""); }} style={comboBtn}>
          <span style={comboBtnText}>
            {sel ? `${sel.title}${sel.kunde_name ? ` · ${sel.kunde_name}` : ""}` : leerLabel}
          </span>
          <span style={{ color: "var(--vg-text-3)", flex: "0 0 auto" }}>▾</span>
        </button>
      ) : (
        <input
          autoFocus={open}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => setOpen(true)}
          /* Verzoegert schliessen: ohne das feuert blur vor dem Klick auf einen
             Vorschlag und die Auswahl geht verloren. */
          onBlur={() => window.setTimeout(() => setOpen(false), 150)}
          placeholder={platzhalter || "Projekt suchen…"}
          style={input}
        />
      )}
      {open && (
        <div style={comboList}>
          {leerLabel && (
            <button onMouseDown={(e) => e.preventDefault()} onClick={() => { onChange(""); setOpen(false); setQ(""); }} style={{ ...comboItem, fontWeight: 600 }}>
              {leerLabel}
            </button>
          )}
          {list.length === 0 ? <div style={{ padding: "8px 10px", color: "var(--vg-text-3)", fontSize: 13 }}>Keins gefunden.</div> :
            list.map((p) => (
              <button key={p.id} onMouseDown={(e) => e.preventDefault()} onClick={() => { onChange(p.id); setOpen(false); setQ(""); }} style={comboItem}>
                <span style={{ fontWeight: 600 }}>{p.title}</span>{p.kunde_name ? <span style={{ color: "var(--vg-text-3)" }}> · {p.kunde_name}</span> : null}
                {istRuhend(p.status) ? <span style={ruhendChip}>{p.status || "abgeschlossen"}</span> : null}
              </button>
            ))}
          {onNeu && (
            <button onMouseDown={(e) => e.preventDefault()} onClick={() => { const v = q.trim(); setOpen(false); setQ(""); onNeu(v); }} style={comboNeu}>
              ＋ {q.trim() ? `„${q.trim()}" neu anlegen` : "Neues Projekt anlegen"}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ── Kunden-Suchfeld ────────────────────────────────────────────────────────
/* Eigene Komponente statt ProjektCombo mit generischem Typ: die Zeile zeigt
   Ort und Ansprechpartner mit an, und danach sucht man auch — bei „Müller"
   hilft „Dresden" mehr als die Kundennummer. */
export function KundenCombo({ kunden, value, onChange, onNeu }: {
  kunden: Kunde[]; value: string; onChange: (id: string) => void;
  /** Siehe ProjektCombo: anlegen, was die Suche nicht findet. */
  onNeu?: (vorschlag: string) => void;
}) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const sel = kunden.find((k) => k.id === value);
  const list = useMemo(() => {
    const n = q.trim().toLowerCase();
    const treffer = n
      ? kunden.filter((k) => `${k.name} ${k.ort} ${k.ansprechpartner}`.toLowerCase().includes(n))
      : kunden;
    return treffer.slice(0, 60);
  }, [kunden, q]);
  return (
    <div style={{ position: "relative" }}>
      {sel && !open ? (
        <button onClick={() => { setOpen(true); setQ(""); }} style={comboBtn}>
          <span style={comboBtnText}>{sel.name}{sel.ort ? ` · ${sel.ort}` : ""}</span>
          <span style={{ color: "var(--vg-text-3)", flex: "0 0 auto" }}>▾</span>
        </button>
      ) : (
        <input
          autoFocus={open}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => setOpen(true)}
          onBlur={() => window.setTimeout(() => setOpen(false), 150)}
          placeholder="Kunde suchen…"
          style={input}
        />
      )}
      {open && (
        <div style={comboList}>
          {list.length === 0 ? <div style={{ padding: "8px 10px", color: "var(--vg-text-3)", fontSize: 13 }}>Keinen gefunden.</div> :
            list.map((k) => (
              <button key={k.id} onMouseDown={(e) => e.preventDefault()} onClick={() => { onChange(k.id); setOpen(false); setQ(""); }} style={comboItem}>
                <span style={{ fontWeight: 600 }}>{k.name}</span>
                {k.ort ? <span style={{ color: "var(--vg-text-3)" }}> · {k.ort}</span> : null}
              </button>
            ))}
          {onNeu && (
            <button onMouseDown={(e) => e.preventDefault()} onClick={() => { const v = q.trim(); setOpen(false); setQ(""); onNeu(v); }} style={comboNeu}>
              ＋ {q.trim() ? `„${q.trim()}" neu anlegen` : "Neuen Kunden anlegen"}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/* Umschalter Einsatz | Besichtigung. Zwei Knoepfe statt eines Auswahlfelds:
   es sind genau zwei Faelle, und der gewaehlte muss auf einen Blick zu sehen
   sein — davon haengt ab, ob darunter ein Projekt oder ein Kunde steht. */
export function ArtSchalter({ wert, onWahl }: { wert: "termin" | "besichtigung"; onWahl: (w: "termin" | "besichtigung") => void }) {
  return (
    <div className="pl-art">
      <button type="button" className={`pl-art-btn${wert === "termin" ? " pl-art-an" : ""}`} onClick={() => onWahl("termin")}>
        Einsatz am Projekt
      </button>
      <button type="button" className={`pl-art-btn${wert === "besichtigung" ? " pl-art-an" : ""}`} onClick={() => onWahl("besichtigung")}>
        Besichtigung beim Kunden
      </button>
    </div>
  );
}
